/**
 * Time bucketing that mirrors the backend's stats queries: periods are
 * `DATE_TRUNC(step, …)` in UTC and only buckets with data are returned, so the
 * client fills in the empty ones.
 */

export type Period = 'since' | 'year' | 'month' | 'day';
export type Step = 'year' | 'month' | 'day';

/** Bucket size the backend uses by default for a period. */
export function stepFor(period: Period): Step {
  return period === 'year' || period === 'since' ? 'month' : 'day';
}

/** Normalises a backend period (`2025-03-01T00:00:00Z`) to `2025-03-01`. */
export function bucketKey(period: string): string {
  return period.slice(0, 10);
}

/** Parses a bucket key back into a UTC date. */
export function bucketDate(key: string): Date {
  return new Date(`${key}T00:00:00Z`);
}

function truncate(date: Date, step: Step): Date {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      step === 'year' ? 0 : date.getUTCMonth(),
      step === 'day' ? date.getUTCDate() : 1,
    ),
  );
}

function advance(date: Date, step: Step): Date {
  const d = new Date(date);
  if (step === 'year') d.setUTCFullYear(d.getUTCFullYear() + 1);
  else if (step === 'month') d.setUTCMonth(d.getUTCMonth() + 1);
  else d.setUTCDate(d.getUTCDate() + 1);
  return d;
}

/** Every bucket key from `start` to `end` inclusive. */
export function bucketKeys(start: Date, end: Date, step: Step): string[] {
  const keys: string[] = [];
  const last = truncate(end, step).getTime();
  for (let d = truncate(start, step); d.getTime() <= last; d = advance(d, step))
    keys.push(d.toISOString().slice(0, 10));
  return keys;
}

/**
 * All bucket keys for a selected period, including empty ones. `fromDate` is
 * the reference date sent to the backend (its local year/month select the
 * period). Buckets after `now` are dropped so the current period doesn't end
 * in a run of misleading zeros; "since" starts at the first bucket with data.
 */
export function periodBuckets(
  period: Period,
  fromDate: Date,
  dataKeys: readonly string[],
  step: Step = stepFor(period),
  now: Date = new Date(),
): string[] {
  const y = fromDate.getFullYear();
  const m = fromDate.getMonth();
  const sorted = [...dataKeys].sort();

  let start: Date;
  let end: Date;
  switch (period) {
    case 'year':
      start = new Date(Date.UTC(y, 0, 1));
      end = new Date(Date.UTC(y, 11, 31));
      break;
    case 'month':
      start = new Date(Date.UTC(y, m, 1));
      end = new Date(Date.UTC(y, m + 1, 0));
      break;
    case 'day':
      start = new Date(Date.UTC(y, m, fromDate.getDate()));
      end = start;
      break;
    case 'since':
      if (sorted.length === 0) return [];
      start = bucketDate(sorted[0]);
      end = now;
      break;
  }

  if (end > now) end = now;
  const keys = end >= start ? bucketKeys(start, end, step) : [];

  // Never drop data that falls outside the computed range.
  const known = new Set(keys);
  const extra = sorted.filter((k) => !known.has(k));
  return extra.length ? [...keys, ...extra].sort() : keys;
}
